import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import IconButton from 'components/common/IconButton';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import AdvanceTableSearchBox from 'components/common/advance-table/AdvanceTableSearchBox';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import { deleteFile, fetchKnowledgeBases, uploadFile } from 'services/elvis-knowledge';
import dayjs from 'dayjs';

const buildDeletePayload = row => {
    return {
        vectorStoreId: row.vectorStoreId,
        fileId: row.openAiFileId
    }
};
const normalizeKnowledgeBase = item => ({
    id: item?.id,
    name: item?.knowledgeName ?? 'N/A',
    versionNo: item?.versionNo,
    sourceFileName: item?.sourceFileName,
    sourceFilePath: item?.sourceFilePath,
    openAiFileId: item?.openAiFileId,
    vectorStoreId: item?.vectorStoreId,
    active: item?.isActive ?? false,
    setupStatus: item?.setupStatus,
    notes: item?.notes ?? '—',
    createdBy: item?.createdBy ?? 'N/A',
    createdDateRaw: item?.createdDate,
    createdDate: item?.createdDate ? dayjs(item?.createdDate).format('DD/MM/YYYY HH:mm:ss') : 'N/A',
    updatedBy: item?.updatedBy ?? 'N/A',
    updatedDate: item?.updatedDate ? dayjs(item?.updatedDate).format('DD/MM/YYYY HH:mm:ss') : 'N/A',
    raw: item
});
const extractArrayPayload = payload => {
    if (Array.isArray(payload))
        return payload;
    if (!payload || typeof payload !== 'object')
        return [];
    const candidates = [
        'data',
        'items',
        'result',
        'knowledgeBases',
        'value',
        'records'
    ];
    for (const key of candidates) {
        const value = payload[key];
        if (Array.isArray(value))
            return value;
        if (value && typeof value === 'object') {
            const nested = extractArrayPayload(value);
            if (nested.length)
                return nested;
        }
    }
    return [];
};
const defaultForm = {
    file: null,
    knowledgeName: '',
    notes: '',
    isActive: true
};
const ElvisKnowledgeBaseManagement = () => {
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState(null);
    const tableContainerRef = useRef(null);
    const detailsCardRef = useRef(null);
    const [showActiveOnly, setShowActiveOnly] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [uploadMessage, setUploadMessage] = useState('');
    const [deleteMessage, setDeleteMessage] = useState('');
    const [uploadValidated, setUploadValidated] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [uploadForm, setUploadForm] = useState(defaultForm);

    const filteredRows = rows.filter(row => !showActiveOnly || row.active);
    const loadKnowledgeBases = async () => {
        try {
            setLoading(true);
            setError('');
            setUploadMessage('');
            const payload = await fetchKnowledgeBases();
            const data = extractArrayPayload(payload);
            const normalized = data.map(normalizeKnowledgeBase);
            setRows(normalized);
            setSelectedRow(null);
        } catch (err) {
            setError('Unable to load knowledge bases.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadKnowledgeBases();
        const handleClickOutside = event => {
            const clickedInsideTable = tableContainerRef.current && tableContainerRef.current.contains(event.target);
            const clickedInsideDetails = detailsCardRef.current && detailsCardRef.current.contains(event.target);
            const clickedInsideModals = event.target instanceof Node && event.target.closest('.modal');
            if (!clickedInsideTable && !clickedInsideDetails && !clickedInsideModals)
                setSelectedRow(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    useEffect(() => {
        if (selectedRow && !filteredRows.some(row => row.id === selectedRow.id))
            setSelectedRow(null);
    }, [filteredRows, selectedRow]);

    const handleUploadClick = () => {
        setUploadMessage('');
        setUploadError('');
        setUploadValidated(false);
        setShowUploadModal(true);
    };
    const handleUploadModalClose = () => {
        if (uploading)
            return;
        setShowUploadModal(false);
        setUploadError('');
        setUploadValidated(false);
        setUploadForm(defaultForm);
    };
    const handleUploadFormChange = event => {
        const { name, type, checked, value, files } = event.target;
        if (uploadError)
            setUploadError('');

        setUploadForm(prev => ({
            ...prev,
            [name]:
                type === 'checkbox'
                    ? checked
                    : type === 'file'
                        ? files?.[0] ?? null
                        : value
        }));
    };
    const handleFileUpload = async event => {
        event.preventDefault();
        const form = event.currentTarget;
        if (form.checkValidity() === false) {
            event.stopPropagation();
            setUploadValidated(true);
            return;
        }
        try {
            setUploading(true);
            setUploadError('');
            setUploadValidated(true);
            setUploadMessage('');
            const formData = new FormData();
            formData.append('KnowledgeName', uploadForm.knowledgeName.trim());
            formData.append('Notes', uploadForm.notes.trim());
            formData.append('SetActive', String(uploadForm.isActive));
            formData.append('File', uploadForm.file);
            const success = await uploadFile(formData);
            if (success) {
                setUploadMessage('Knowledge base uploaded successfully.');
                await loadKnowledgeBases();
                handleUploadModalClose();
            }
        } catch (err) {
            console.log(err)
            setUploadError('Upload failed.');
        } finally {
            setUploading(false);
        }
    };
    const handleDeleteClick = () => {
        setDeleteError('');
        setDeleteMessage('');
        setShowDeleteModal(true);
    };
    const handleDeleteModalClose = () => {
        if (deleting)
            return;
        setDeleteError('');
        setShowDeleteModal(false);
    };
    const handleFileDelete = async event => {
        event.preventDefault();
        if (!selectedRow)
            return;
        try {
            setDeleting(true);
            setDeleteError('');
            const success = await deleteFile(buildDeletePayload(selectedRow));
            if (!success)
                throw new Error('Failed to delete file.');
            setDeleteMessage('Knowledge base file deleted successfully.');
            await loadKnowledgeBases();
            setSelectedRow(null);
            handleDeleteModalClose();
        } catch (err) {
            console.log(err)
            setDeleteError('Failed to delete file.');
        } finally {
            setDeleting(false);
        }
    };
    const columns = useMemo(() => [
        {
            accessor: 'name',
            Header: 'Knowledge Name',
            headerProps: { className: 'text-900' }
        },
        {
            accessor: row => (row.active ? '✅' : '❌'),
            Header: 'Active',
            headerProps: { className: 'text-900' }
        },
        {
            accessor: 'createdDate',
            Header: 'Created Date',
            sortType: (rowA, rowB) => {
                const a = new Date(rowA.original.createdDateRaw || 0).getTime();
                const b = new Date(rowB.original.createdDateRaw || 0).getTime();
                return a - b;
            },
            headerProps: { className: 'text-900' }
        }
    ], []);
    return (
        <Row>
            <Col>
                <AdvanceTableWrapper
                    columns={columns}
                    data={filteredRows}
                    sortable
                    pagination={filteredRows.length > 0 ? filteredRows.length : 1000}
                    perPage={filteredRows.length > 0 ? filteredRows.length : 1000}
                    globalFilterColumns={["name"]}>
                    <Card ref={tableContainerRef} className="mb-3">
                        <Card.Header className="pb-2">
                            <Row className="align-items-start justify-content-between">
                                <Col xs="auto" className="align-items-bottom">
                                    <div className="form-check form-switch">
                                        <input className="form-check-input" type="checkbox" id="showActiveOnly" checked={showActiveOnly} onChange={e => setShowActiveOnly(e.target.checked)} />
                                        <label className="form-check-label" htmlFor="showActiveOnly"> Show Active Only</label>
                                    </div>
                                </Col>
                                <Col md="auto" sm={6} lg={4}>
                                    <AdvanceTableSearchBox table placeholder="Search..." />
                                </Col>
                            </Row>
                        </Card.Header>
                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="p-3">Loading knowledge bases...</div>
                            ) : (
                                <AdvanceTable
                                    table
                                    headerClassName="bg-200 text-nowrap align-middle"
                                    rowClassName="align-middle white-space-nowrap cursor-pointer"
                                    tableProps={{
                                        bordered: true,
                                        striped: true,
                                        size: 'sm',
                                        className: 'fs-10 mb-0 overflow-hidden'
                                    }}
                                    handleClickRow={(e, row) => setSelectedRow(row.original)}
                                    selectedRowId={
                                        selectedRow?.id != null ? String(selectedRow.id) : null
                                    }
                                    selectedRowClassName="table-active"
                                />
                            )}
                        </Card.Body>
                    </Card>
                </AdvanceTableWrapper>
            </Col>
            <Col>
                {error ? <Alert variant="danger">{error}</Alert> : null}
                <div
                    id="orders-actions"
                    className="pb-2"
                    style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton
                        variant="falcon-default"
                        size="sm"
                        icon="file"
                        transform="shrink-3"
                        onClick={handleUploadClick}
                        disabled={uploading}>
                        <span className="d-none d-sm-inline-block ms-1">
                            {uploading ? 'Uploading...' : 'Upload'}
                        </span>
                    </IconButton>
                </div>
                <Card ref={detailsCardRef}>
                    <Card.Header>
                        <strong>
                            {selectedRow?.name || 'Knowledge Base File Details'}
                        </strong>
                    </Card.Header>
                    <Card.Body>
                        {uploadMessage ? (
                            <Alert variant="success">{uploadMessage}</Alert>
                        ) : null}
                        {deleteMessage ? (
                            <Alert variant="success">{deleteMessage}</Alert>
                        ) : null}
                        {selectedRow ? (
                            <div>
                                <p>
                                    <strong>File Name:</strong>{' '}
                                    {selectedRow.sourceFileName || 'N/A'}
                                </p>
                                <p>
                                    <strong>File Path:</strong>{' '}
                                    {selectedRow.sourceFilePath || 'N/A'}
                                </p>
                                <p>
                                    <strong>ID:</strong> {selectedRow.id}
                                </p>
                                <p>
                                    <strong>Active:</strong> {selectedRow.active ? 'Yes' : 'No'}
                                </p>
                                <p>
                                    <strong>Version No:</strong> {selectedRow.versionNo || 'N/A'}
                                </p>
                                <p>
                                    <strong>OpenAI File ID:</strong>{' '}
                                    {selectedRow.openAiFileId || 'N/A'}
                                </p>
                                <p>
                                    <strong>Vector Store ID:</strong>{' '}
                                    {selectedRow.vectorStoreId || 'N/A'}
                                </p>
                                <p>
                                    <strong>Setup Status:</strong>{' '}
                                    {selectedRow.setupStatus || 'N/A'}
                                </p>
                                <p>
                                    <strong>Notes:</strong> {selectedRow.notes || 'N/A'}
                                </p>
                                <p>
                                    <strong>Created By:</strong> {selectedRow.createdBy || 'N/A'}
                                </p>
                                <p>
                                    <strong>Created Date:</strong>
                                    {selectedRow.createdDate || 'N/A'}
                                </p>
                                <p>
                                    <strong>Updated By:</strong> {selectedRow.updatedBy || 'N/A'}
                                </p>
                                <p>
                                    <strong>Updated Date:</strong>{' '}
                                    {selectedRow.updatedDate || 'N/A'}
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                    <IconButton
                                        variant="falcon-default"
                                        size="sm"
                                        icon="trash"
                                        transform="shrink-3"
                                        style={{
                                            backgroundColor: '#dc3545',
                                            color: 'white',
                                            borderColor: '#dc3545'
                                        }}
                                        onClick={handleDeleteClick}
                                        disabled={deleting}
                                    ></IconButton>
                                </div>
                            </div>
                        ) : (
                            <p>Please select a row to see details.</p>
                        )}
                    </Card.Body>
                </Card>
            </Col>
            <Modal
                show={showUploadModal}
                onHide={handleUploadModalClose}
                centered
                backdrop="static">
                <Form
                    noValidate
                    validated={uploadValidated}
                    onSubmit={handleFileUpload}>
                    <Modal.Header closeButton>
                        <Modal.Title as="h5">Upload to Knowledge Base</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {uploadError ? (
                            <div className="text-danger mb-3">{uploadError}</div>
                        ) : null}
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="knowledgeFile">File</Form.Label>
                            <Form.Control
                                id="knowledgeFile"
                                name="file"
                                type="file"
                                accept=".txt,.md,.json,.csv,.pdf,.docx"
                                onChange={handleUploadFormChange}
                                disabled={uploading}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Please choose a file to upload.
                            </Form.Control.Feedback>
                            <Form.Label className="form-text text-muted">
                                Accepted file types: .txt, .md, .json, .csv, .pdf, .docx
                            </Form.Label>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="knowledgeName">Knowledge Name</Form.Label>
                            <Form.Control
                                id="knowledgeName"
                                name="knowledgeName"
                                type="text"
                                value={uploadForm.knowledgeName}
                                onChange={handleUploadFormChange}
                                placeholder="Enter a knowledge base name"
                                disabled={uploading}
                                required
                            />
                            <Form.Control.Feedback type="invalid">
                                Please enter a Knowledge Name.
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="knowledgeNotes">Notes</Form.Label>
                            <Form.Control
                                id="knowledgeNotes"
                                name="notes"
                                as="textarea"
                                rows={3}
                                value={uploadForm.notes}
                                onChange={handleUploadFormChange}
                                placeholder="Add an optional note"
                                disabled={uploading}
                            />
                        </Form.Group>
                        <Form.Check
                            id="knowledgeActive"
                            name="isActive"
                            type="switch"
                            label="Active"
                            checked={uploadForm.isActive}
                            onChange={handleUploadFormChange}
                            disabled={uploading}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="outline-secondary"
                            onClick={handleUploadModalClose}
                            disabled={uploading}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={uploading}>
                            {uploading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
            <Modal
                show={showDeleteModal}
                onHide={handleDeleteModalClose}
                centered
                backdrop="static">
                <Modal.Body>
                    {deleteError ? <Alert variant="danger">{deleteError}</Alert> : null}
                    <p className="mb-0">
                        Are you sure you want to delete{' '}
                        <strong>{selectedRow?.name || 'this knowledge file'}</strong>?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={handleDeleteModalClose}
                        disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        disabled={deleting}
                        onClick={handleFileDelete}>
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Row>
    );
};
export default ElvisKnowledgeBaseManagement;